import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Alert, Row, Col, Button } from 'react-bootstrap';
import { gql } from '@apollo/client';
import { useQuery } from "@apollo/client/react";
import {compose} from '../../lib/compose';
import { faCircleCheck } from '@fortawesome/sharp-light-svg-icons';
import { faCircleCheck as faCircleCheckSolid } from '@fortawesome/sharp-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {useAtom} from 'jotai';
import withAlerts from '../../lib/withAlerts';
import { getClaims } from '../auth/sessionStore';
import { useMessageMutations } from './messageMutations';
import { acknowledgedIds } from '../recoil';
import cx from 'classnames';

const CATEGORY_LABELS = {
  FW: 'Firmware Update',
  MW: 'Maintenance Window',
  NP: 'Noncompliant Policy',
};

const formatCategory = (category) => CATEGORY_LABELS[category] ?? 'Update';

export const SystemMessages = ({ alerts }) => {
  const { error } = alerts;
  const { preferred_username: username } = getClaims() || {};
  const { acknowledge } = useMessageMutations();

  const [acked, setAcked] = useAtom(acknowledgedIds);
  const acknowledgedSet = useMemo(() => new Set(acked), [acked]);

  const [panelOpen, setPanelOpen] = useState(true);
  const panelOpenRef = useRef(true);
  const setPanelOpenBoth = useCallback((value) => {
    panelOpenRef.current = value;
    setPanelOpen(value);
  }, []);
  const lastSeenIdsRef = useRef(new Set());
  const [pendingIds, setPendingIds] = useState(new Set());
  const [isBulkPending, setIsBulkPending] = useState(false);
  
  const messageFilters = useMemo(() => ({
    combineAnd: [
      { field: 'with.system_message_admin_actions', value: '1' },
      { field: 'system_message_admin_action.username', value: username },
    ],
  }), [username]);

  const { data } = useQuery(MESSAGES, {
    variables: { filters: messageFilters },
    pollInterval: 60_000,
    skip: !username,
  });

  const allMessages = data?.SystemMessages?.result ?? [];

  // Active = within time window and not yet acknowledged (server or local)
  const activeMessages = useMemo(() => {
    const ts = Math.floor(Date.now() / 1000);
    return allMessages.filter(({ id, start_time, end_time, system_message_admin_actions }) => {
      const ackedOnServer = system_message_admin_actions?.some((a) => a?.username === username);
      const ackedLocally = acknowledgedSet.has(id);
      return ts >= start_time && ts <= end_time && !ackedOnServer && !ackedLocally;
    });
  }, [allMessages, username, acknowledgedSet]);
 
  // Reopen the panel only when a genuinely new message ID arrives via poll.
  // Uses a ref for panelOpen so this effect doesn't need it as a dependency
  // (avoiding the loop: new message → open panel → effect re-runs → open panel…).
  useEffect(() => {
    const currentIds = new Set(activeMessages.map(({ id }) => id));
    const hasNewMessage = activeMessages.some(({ id }) => !lastSeenIdsRef.current.has(id));
 
    if (hasNewMessage && !panelOpenRef.current) {
      setPanelOpenBoth(true);
    }
 
    lastSeenIdsRef.current = currentIds;
  }, [activeMessages, setPanelOpenBoth]);
 
  // X button — session-only hide, no mutation
  const handleIgnore = useCallback(() => {
    setPanelOpenBoth(false);
  }, [setPanelOpenBoth]);
 
  // Single row click — fires mutation, optimistic UI via pendingIds
  const handleAcknowledge = useCallback(async (id) => {
    if (isBulkPending || pendingIds.has(id) || acknowledgedSet.has(id)) return;
 
    setPendingIds((prev) => new Set(prev).add(id));
 
    try {
      const result = await acknowledge({
        variables: { ack: { system_message_id: id, action: 'A' } },
      });
 
      if (result.errors) {
        error({ msg: 'There was an error acknowledging the message. Please try again.' });
        setPendingIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
        return;
      }
 
      setAcked((prev) => [...new Set([...prev, id])]);
      setPendingIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    } catch {
      error({ msg: 'There was an error acknowledging the message. Please try again.' });
      setPendingIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    }
  }, [isBulkPending, pendingIds, acknowledgedSet, acknowledge, error, setAcked]);
 
  // Acknowledge All — filters out already-pending/acked, locks UI while in-flight
  const handleAcknowledgeAll = useCallback(async () => {
    const targets = activeMessages.filter(
      ({ id }) => !pendingIds.has(id) && !acknowledgedSet.has(id)
    );
 
    if (targets.length === 0) return;
 
    setIsBulkPending(true);
    setPendingIds((prev) => new Set([...prev, ...targets.map(({ id }) => id)]));
 
    const results = await Promise.allSettled(
      targets.map(({ id }) =>
        acknowledge({ variables: { ack: { system_message_id: id, action: 'A' } } })
      )
    );
 
    const succeededIds = new Set();
    results.forEach((result, i) => {
      if (result.status === 'fulfilled' && !result.value?.errors) {
        succeededIds.add(targets[i].id);
      }
    });
 
    const allSucceeded = succeededIds.size === targets.length;
 
    setPendingIds((prev) => {
      const next = new Set(prev);
      targets.forEach(({ id }) => next.delete(id));
      return next;
    });
    setIsBulkPending(false);
 
    if (!allSucceeded) {
      error({ msg: 'Some messages could not be acknowledged. Please try again.' });
    }
 
    if (succeededIds.size > 0) {
      setAcked((prev) => [...new Set([...prev, ...succeededIds])]);
 
      // Close the panel only when every message was successfully acknowledged
      if (allSucceeded) setPanelOpenBoth(false);
    }
  }, [activeMessages, pendingIds, acknowledgedSet, acknowledge, error, setAcked, setPanelOpenBoth]);
 
  const isAcknowledged = (id) => acknowledgedSet.has(id) || pendingIds.has(id);
 
  if (!activeMessages.length || !panelOpen) return null;

  return (
    <Alert id="system-messages" variant="warning" onClose={handleIgnore} dismissible>
      <Alert.Heading className="pb-3">Notifications</Alert.Heading>
      <div className="container-fluid">
        {activeMessages.map(({ id, message, category }) => (
          <Row
            key={id}
            className={cx('system-message-row', { 'pe-none': isBulkPending })}
            onClick={() => handleAcknowledge(id)}
          >
            <Col>
              <div className={cx('bordered-row py-3', { hide: isAcknowledged(id) })}>
                <label data-messageid={id} className="icon-label">
                  <div className="icon-labels d-inline">
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      size="lg"
                      className={cx('inactive-icon', { checked: isAcknowledged(id) })}
                    />
                    <FontAwesomeIcon
                      icon={faCircleCheckSolid}
                      size="lg"
                      className={cx('active-icon', { checked: isAcknowledged(id) })}
                      color="black"
                    />
                  </div>
                  <div className="d-inline ms-4">
                    <b>{formatCategory(category)}:</b> {message}
                  </div>
                </label>
              </div>
            </Col>
          </Row>
        ))}

        {activeMessages.length > 1 && (
          <Row className="py-3 ack-all-row">
            <Col className="px-0">
              <Button
                id="acknowledge"
                className="px-3 btn-black"
                onClick={handleAcknowledgeAll}
                disabled={isBulkPending}
              >
                {isBulkPending ? 'Acknowledging…' : 'Acknowledge All'}
              </Button>
            </Col>
          </Row>
        )}
      </div>
    </Alert>
  );
};

export const MESSAGES = gql`
  query getSystemMessages($filters: GenericFilters) {
    SystemMessages(filters: $filters) {
      result {
        id
        category
        message
        start_time
        end_time
        system_message_admin_actions {
          username
        }
      }
    }
  }
`;

export default compose(withAlerts)(SystemMessages);
